export type OnboardingStage = "name" | "purpose" | "metrics" | "preferences" | "confirm" | "complete";
export type AssistantVoice = "female" | "male";
export type PersonaGender = "female" | "male";

export interface AssistantMessage { role: "user" | "assistant"; content: string; createdAt: string }
export interface AssistantMemory { fact: string; kind: string; createdAt: string }
export interface AssistantState {
  stage: OnboardingStage;
  voice: AssistantVoice | null;
  personaGender: PersonaGender | null;
  profile: Record<string, string | number | boolean | string[]>;
  memories: AssistantMemory[];
  messages: AssistantMessage[];
  onboardingMode: "first" | "repeat";
}
export interface AssistantTurn {
  reply: string;
  nextStage: OnboardingStage;
  profilePatch: Record<string, string | number | boolean | string[]>;
  memoryUpdates: Array<{ fact: string; kind: string }>;
  mealIntent: "none" | "ambiguous" | "confirmed";
  mealText: string | null;
}

const STORAGE_KEY = "yumnote.assistant.prototype.v3";
export const initialAssistantState = (): AssistantState => ({ stage: "name", voice: null, personaGender: null, profile: {}, memories: [], messages: [], onboardingMode: "first" });
const identityProfileKeys = new Set(["displayName", "personaGender", "age", "formulaSex"]);

function ageFromMemories(memories: AssistantMemory[]): number | null {
  for (const memory of [...memories].reverse()) {
    const match = memory.fact.toLocaleLowerCase("ru-RU").match(/возраст[^\d]{0,12}(\d{1,3})\s*(?:лет|года|год)?/u);
    const age = match ? Number(match[1]) : NaN;
    if (Number.isFinite(age) && age >= 14 && age <= 120) return age;
  }
  return null;
}

export function loadAssistantState(): AssistantState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? { ...initialAssistantState(), ...JSON.parse(raw) as AssistantState } : initialAssistantState();
  } catch { return initialAssistantState(); }
}

export function saveAssistantState(state: AssistantState): void { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); }
export function resetAssistantState(): AssistantState { const state = initialAssistantState(); saveAssistantState(state); return state; }
export function memoryBytes(value: unknown): number { return new TextEncoder().encode(JSON.stringify(value)).byteLength; }

export function restartAssistantDataCollection(state: AssistantState): AssistantState {
  const hadGoalData = state.stage === "complete" || ["age", "heightCm", "weightKg", "goal", "targetWeightKg", "targetDate"].some(key => state.profile[key] !== undefined);
  const profile = Object.fromEntries(Object.entries(state.profile).filter(([key]) => identityProfileKeys.has(key)));
  const rememberedAge = typeof profile.age === "number" ? profile.age : ageFromMemories(state.memories);
  if (rememberedAge !== null) profile.age = rememberedAge;
  return {
    ...state,
    stage: "metrics",
    profile,
    memories: state.memories.filter(memory => memory.kind === "identity"),
    messages: [],
    onboardingMode: hadGoalData || state.onboardingMode === "repeat" ? "repeat" : "first",
  };
}

export function assistantIntroReply(state: AssistantState): string {
  return state.onboardingMode === "repeat" ? "давай настроим новую цель." : "давай настроим первую цель.";
}

export function assistantOpeningReply(state: AssistantState): string {
  const name = typeof state.profile.displayName === "string" ? state.profile.displayName.trim() : "";
  switch (state.stage) {
    case "name":
      return "Давай познакомимся. Как тебя зовут? Фамилия не нужна — достаточно имени.";
    case "purpose":
      return `${name ? `${name}, ` : ""}для чего ты хочешь использовать YumNote: похудеть, удерживать вес, набрать его или просто следить за питанием?`;
    case "metrics":
      return "Назови одним ответом возраст, рост, текущий вес, что хочешь — похудеть, набрать или сохранить вес, желаемый вес и к какой дате.\n\nНапример: «45 лет, рост 180, вешу 80, хочу похудеть до 70 килограммов к 1 декабря».";
    case "preferences":
      return "Основные данные уже собраны — дополнительных вопросов для запуска не нужно.";
    case "confirm":
      return "Основные данные собраны. Показываю результат.";
    case "complete":
      return `${name ? `${name}, ` : ""}настройка завершена. Если захочешь пересчитать цель, снова нажми S.`;
  }
}

export function assistantVisibleReply(previousStage: OnboardingStage, nextState: AssistantState, reply: string): string {
  const cleanReply = reply.trim();
  if (nextState.stage === "complete" || nextState.stage === previousStage || cleanReply.includes("?")) return cleanReply;
  return `${cleanReply} ${assistantOpeningReply(nextState)}`.trim();
}

export async function requestAssistantTurn(state: AssistantState, text: string): Promise<AssistantTurn> {
  const response = await fetch("https://api.yumnote.ru/v1/assistant/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ stage: state.stage, personaGender: state.personaGender, profile: state.profile, memories: state.memories.slice(-40), messages: state.messages.slice(-16), text }),
  });
  if (!response.ok) throw new Error("assistant_chat_failed");
  return response.json() as Promise<AssistantTurn>;
}

export function applyAssistantTurn(state: AssistantState, text: string, turn: AssistantTurn): AssistantState {
  const now = new Date().toISOString();
  const known = new Set(state.memories.map(memory => `${memory.kind}:${memory.fact.toLocaleLowerCase("ru-RU")}`));
  const additions = turn.memoryUpdates
    .filter(memory => memory.fact.trim().length >= 3 && memory.fact.length <= 280 && !known.has(`${memory.kind}:${memory.fact.toLocaleLowerCase("ru-RU")}`))
    .map(memory => ({ ...memory, createdAt: now }));
  return {
    ...state,
    stage: turn.nextStage,
    profile: { ...state.profile, ...turn.profilePatch },
    memories: [...state.memories, ...additions],
    messages: [...state.messages, { role: "user" as const, content: text, createdAt: now }, { role: "assistant" as const, content: turn.reply, createdAt: now }].slice(-100),
  };
}

export function hasCompleteGoalProfile(profile: AssistantState["profile"]): boolean {
  const numeric = (value: unknown, min: number, max: number) => typeof value === "number" && Number.isFinite(value) && value >= min && value <= max;
  const goal = typeof profile.goal === "string" ? profile.goal.toLocaleLowerCase("ru-RU") : "";
  const maintain = /maintain|сохран|удерж|поддерж/u.test(goal);
  const hasDirection = maintain || /lose|похуд|сброс|сниз|gain|набр|увелич/u.test(goal);
  return numeric(profile.age, 14, 120)
    && numeric(profile.heightCm, 100, 250)
    && numeric(profile.weightKg, 25, 400)
    && hasDirection
    && (maintain || (numeric(profile.targetWeightKg, 25, 400) && (typeof profile.targetDate === "string" || numeric(profile.timeframeMonths, 0.1, 120))));
}
